import { useState, useMemo, useCallback } from 'react';
import { ExpertiseData } from '../types/pipeline.types';
import { sortByUrgencyCategory } from '../utils/urgencyCalculator';
import { isOverdue } from '@/core/utils/date.utils';

export const useStageFilters = (expertises: ExpertiseData[], stageId?: number) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('urgency');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Filter expertises based on search and filters
  const filteredExpertises = useMemo(() => {
    return expertises.filter(expertise => {
      // Search filter
      const matchesSearch = !searchQuery.trim() || 
        expertise.type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        expertise.id?.toString().includes(searchQuery) ||
        expertise.address?.some(addr => addr.toLowerCase().includes(searchQuery.toLowerCase()));
      
      // Active filter
      const matchesFilter = (() => {
        switch (activeFilter) {
          case 'urgent':
            return expertise.isUrgent === true || expertise.urgency?.isUrgent === true;
          case 'delayed':
            return expertise.delayed === true ||
                   expertise.isUrgent === true ||
                   (expertise.deadline && isOverdue(expertise.deadline));
          case 'assigned':
            return expertise.expert && expertise.expert !== 'Non assigné';
          case 'unassigned':
            return !expertise.expert || expertise.expert === 'Non assigné';
          case 'all':
          default:
            return true;
        }
      })();
      
      return matchesSearch && matchesFilter;
    });
  }, [expertises, searchQuery, activeFilter]);
  
  // Sort expertises
  const sortedExpertises = useMemo(() => {
    let sorted = [...filteredExpertises];
    
    switch (sortBy) {
      case 'urgency':
        sorted = sortByUrgencyCategory(filteredExpertises);
        break;
        
      case 'date':
        sorted.sort((a, b) => {
          const dateA = new Date(a.date || 0).getTime();
          const dateB = new Date(b.date || 0).getTime();
          return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        });
        break;
        
      case 'date_rv':
        // Sort by appointment date (date_du_rv)
        sorted.sort((a, b) => {
          const dateA = a.date_du_rv ? new Date(a.date_du_rv).getTime() : 0;
          const dateB = b.date_du_rv ? new Date(b.date_du_rv).getTime() : 0;
          // If no RV date, put at the end
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        });
        break;
        
      case 'expert':
        sorted.sort((a, b) => {
          const expertA = a.expert || 'Non assigné';
          const expertB = b.expert || 'Non assigné';
          const result = expertA.localeCompare(expertB);
          return sortOrder === 'asc' ? result : -result;
        });
        break;
        
      case 'client':
        sorted.sort((a, b) => {
          const clientA = a.client_name || '';
          const clientB = b.client_name || '';
          const result = clientA.localeCompare(clientB);
          return sortOrder === 'asc' ? result : -result;
        });
        break;
        
      case 'amount':
        sorted.sort((a, b) => {
          const amountA = a.forfait || 0;
          const amountB = b.forfait || 0;
          return sortOrder === 'asc' ? amountA - amountB : amountB - amountA;
        });
        break;
        
      case 'type':
        sorted.sort((a, b) => {
          const result = (a.type || '').localeCompare(b.type || '');
          return sortOrder === 'asc' ? result : -result;
        });
        break;
        
      case 'id':
        sorted.sort((a, b) => {
          const idA = String(a.id || '');
          const idB = String(b.id || '');
          const result = idA.localeCompare(idB);
          return sortOrder === 'desc' ? result : -result;
        });
        break;
        
      default:
        break;
    }
    
    return sorted;
  }, [filteredExpertises, sortBy, sortOrder]);
  
  // Reset filters
  const resetFilters = useCallback(() => {
    setSearchQuery('');
    setSortBy('urgency');
    setSortOrder('desc');
    setActiveFilter('all');
  }, []);
  
  // Clear search when stage changes
  const clearSearchAndFilters = useCallback(() => {
    setSearchQuery('');
    setActiveFilter('all');
  }, []);
  
  return {
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    activeFilter,
    setActiveFilter,
    filteredExpertises: sortedExpertises,
    resultsCount: sortedExpertises.length,
    resetFilters,
    clearSearchAndFilters,
    stageId
  };
};